import React, { useState, useEffect, useMemo } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";

const PatientProfileView = () => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, authLoading, authError] = useAuthState(auth);

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 30%, #f0fdf4 100%)",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      paddingTop: "32px",
      paddingBottom: "32px"
    },
    header: { textAlign: "center", marginBottom: "32px" },
    headerTitle: {
      fontSize: "36px",
      fontWeight: "700",
      background: "linear-gradient(135deg, #1f2937, #6b7280)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      margin: "0 0 8px 0"
    },
    headerSubtitle: { fontSize: "16px", color: "#6b7280", margin: 0 },
    mainContent: { maxWidth: "900px", margin: "0 auto", padding: "0 24px" },
    profileHeader: {
  backgroundColor: "white",
  borderRadius: "24px",
  padding: "32px",
  // ⬇️ add this to avoid overlap with the button on the right
  paddingRight: "160px", // space reserved for the edit button
  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
  marginBottom: "24px",
  position: "relative",
  overflow: "hidden"
},

    profileHeaderBg: {
      position: "absolute",
      top: 0,
      right: 0,
      width: "300px",
      height: "100%",
      background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)",
      opacity: 0.3,
      transform: "skewX(-20deg)",
      transformOrigin: "top right"
    },
    profileInfo: { position: "relative", zIndex: 1 },
    profileName: {
  fontSize: "32px",
  fontWeight: "700",
  color: "#111827",
  marginBottom: "8px",
  // ⬇️ allow very long names to line-break instead of pushing
  wordBreak: "break-word",
},

    profileMeta: { display: "flex", flexWrap: "wrap", gap: "24px", marginTop: "16px" },
    profileMetaItem: { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#6b7280" },
    editButton: {
      position: "absolute",
      top: "24px",
      right: "24px",
      padding: "12px 24px",
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      border: "none",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)"
    },
    sectionGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "24px",
      marginBottom: "24px"
    },
    section: {
      backgroundColor: "white",
      borderRadius: "16px",
      padding: "24px",
      boxShadow: "0 4px 15px -3px rgba(0, 0, 0, 0.1)",
      border: "1px solid rgba(0, 0, 0, 0.05)"
    },
    sectionTitle: { fontSize: "18px", fontWeight: "600", color: "#111827", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" },
    sectionContent: { fontSize: "14px", color: "#4b5563", lineHeight: "1.6" },
    infoRow: { display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px", paddingBottom: "12px", borderBottom: "1px solid #f3f4f6" },
    infoLabel: { fontSize: "13px", color: "#6b7280", fontWeight: "500", minWidth: "120px" },
    infoValue: { fontSize: "14px", color: "#111827", textAlign: "right", flex: 1 },
    emptyState: { color: "#9ca3af", fontStyle: "italic" },
    tag: {
      display: "inline-block",
      padding: "4px 12px",
      backgroundColor: "#dbeafe",
      color: "#1e40af",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: "500",
      marginRight: "8px",
      marginBottom: "8px"
    },
    statusBadge: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "600" },
    completeBadge: { backgroundColor: "#dcfce7", color: "#166534" },
    incompleteBadge: { backgroundColor: "#fef3c7", color: "#92400e" },
    emergencySection: { backgroundColor: "#fee2e2", border: "1px solid #fecaca" },
    medicalSection: { backgroundColor: "#dbeafe", border: "1px solid #93c5fd" },
    insuranceSection: { backgroundColor: "#dcfce7", border: "1px solid #86efac" },
    lifestyleSection: { backgroundColor: "#f3e8ff", border: "1px solid #e9d5ff" },
    preferencesSection: { backgroundColor: "#e0e7ff", border: "1px solid #c7d2fe" },
    loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px", flexDirection: "column" },
    spinner: { width: "40px", height: "40px", border: "3px solid #e5e7eb", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" },
    errorContainer: { backgroundColor: "#fee2e2", border: "1px solid #fecaca", borderRadius: "12px", padding: "16px", marginBottom: "24px", color: "#dc2626" },
    backButton: {
      padding: "12px 24px",
      backgroundColor: "#f3f4f6",
      color: "#374151",
      border: "1px solid #d1d5db",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginBottom: "24px",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px"
    }
  };

  useEffect(() => {
    if (authLoading) return;

    if (authError) {
      setError("Authentication error: " + authError.message);
      setLoading(false);
      return;
    }

    if (!user) {
      setError("Please sign in to view your profile");
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            setProfileData({
              ...data,
              profileCompletedAt: data.profileCompletedAt?.toDate?.() || data.profileCompletedAt || null,
              lastUpdated: data.lastUpdated?.toDate?.() || data.lastUpdated || new Date()
            });
          } else {
            setProfileData({
              fullName: "",
              email: user.email || "",
              profileCompletedAt: null,
              lastUpdated: new Date()
            });
          }
          setError("");
        } catch (err) {
          console.error("Error processing profile data:", err);
          setError("Error loading profile data");
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error("Error fetching profile:", err);
        setError("Unable to load profile. Please check your connection.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading, authError]);

  const formatDate = (value) => {
    if (!value) return "Not provided";
    let date = value;
    if (value instanceof Date) {
      date = value;
    } else if (typeof value === "string" || typeof value === "number") {
      date = new Date(value);
    } else if (value?.toDate) {
      date = value.toDate();
    } else {
      return "Invalid date";
    }
    if (isNaN(date.getTime())) return "Invalid date";
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  const completeness = useMemo(() => {
    if (!profileData) return 0;
    const fields = [
      "fullName",
      "dob",
      "gender",
      "phone",
      "email",
      "address.street",
      "address.city",
      "address.state",
      "emergencyContact.name",
      "emergencyContact.phone",
      "bloodType",
      "height",
      "weight",
      "insuranceProvider",
      "policyNumber",
      "preferredLanguage",
      "occupation"
    ];
    let completed = 0;
    for (const field of fields) {
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        if (profileData?.[parent]?.[child]) completed++;
      } else if (profileData?.[field]) {
        completed++;
      }
    }
    return Math.round((completed / fields.length) * 100);
  }, [profileData]);

  if (authLoading || loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={{ marginTop: "16px", color: "#6b7280" }}>
            {authLoading ? "Authenticating..." : "Loading profile..."}
          </p>
        </div>
        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.mainContent}>
          <div style={styles.errorContainer}>
            <p>⚠️ {error}</p>
            {!user && (
              <button
                onClick={() => navigate("/login")}
                style={{ ...styles.backButton, backgroundColor: "#3b82f6", color: "white", marginTop: "12px" }}
              >
                Sign In
              </button>
            )}
          </div>
          <button onClick={() => navigate("/dashboard-patient")} style={styles.backButton}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }
        .edit-button:hover {
          background: linear-gradient(135deg, #1d4ed8, #4338ca) !important;
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.4) !important;
        }
        .back-button:hover { background-color: #e5e7eb !important; }
      `}</style>

      <div style={styles.mainContent}>
        <button onClick={() => navigate("/dashboard-patient")} style={styles.backButton} className="back-button">
          ← Back to Dashboard
        </button>

        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>My Profile</h1>
          <p style={styles.headerSubtitle}>View and manage your medical information</p>
        </div>

        {/* Profile Header Card */}
        <div style={styles.profileHeader}>
          <div style={styles.profileHeaderBg}></div>
          <button onClick={() => navigate("/patient-profile-setup")} style={styles.editButton} className="edit-button">
            ✏️ Edit Profile
          </button>

          <div style={styles.profileInfo}>
            <h2 style={styles.profileName}>{profileData?.fullName || "Complete your profile"}</h2>

            <div
              style={{
                ...styles.statusBadge,
                ...(completeness === 100 ? styles.completeBadge : styles.incompleteBadge)
              }}
            >
              {completeness === 100 ? "✅" : "🔄"} Profile {completeness}% Complete
            </div>

            <div style={styles.profileMeta}>
              {profileData?.phone && <div style={styles.profileMetaItem}>📞 {profileData.phone}</div>}
              {profileData?.email && <div style={styles.profileMetaItem}>📧 {profileData.email}</div>}
              {profileData?.dob && <div style={styles.profileMetaItem}>🎂 {formatDate(profileData.dob)}</div>}
              {profileData?.address?.city && (
                <div style={styles.profileMetaItem}>
                  📍 {profileData.address.city}
                  {profileData.address.state ? `, ${profileData.address.state}` : ""}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Sections */}
        <div style={styles.sectionGrid}>
          {/* Personal Information */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>👤 Personal Information</h3>
            <div style={styles.sectionContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Full Name</span>
                <span style={styles.infoValue}>{profileData?.fullName || <span style={styles.emptyState}>Not provided</span>}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Date of Birth</span>
                <span style={styles.infoValue}>
                  {profileData?.dob ? formatDate(profileData.dob) : <span style={styles.emptyState}>Not provided</span>}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Gender</span>
                <span style={styles.infoValue}>
                  {profileData?.gender
                    ? profileData.gender.charAt(0).toUpperCase() + profileData.gender.slice(1)
                    : <span style={styles.emptyState}>Not provided</span>}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Occupation</span>
                <span style={styles.infoValue}>{profileData?.occupation || <span style={styles.emptyState}>Not provided</span>}</span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>📞 Contact Information</h3>
            <div style={styles.sectionContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Phone</span>
                <span style={styles.infoValue}>{profileData?.phone || <span style={styles.emptyState}>Not provided</span>}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Email</span>
                <span style={styles.infoValue}>{profileData?.email || <span style={styles.emptyState}>Not provided</span>}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Address</span>
                <span style={styles.infoValue}>
                  {profileData?.address?.street ? (
                    <div style={{ textAlign: "right" }}>
                      <div>{profileData.address.street}</div>
                      <div>
                        {profileData.address.city}
                        {profileData.address.state ? `, ${profileData.address.state}` : ""}{" "}
                        {profileData.address.zipCode || ""}
                      </div>
                      {profileData.address.country && <div>{profileData.address.country}</div>}
                    </div>
                  ) : (
                    <span style={styles.emptyState}>Not provided</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          {profileData?.emergencyContact?.name && (
            <div style={{ ...styles.section, ...styles.emergencySection }}>
              <h3 style={styles.sectionTitle}>🚨 Emergency Contact</h3>
              <div style={styles.sectionContent}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Name</span>
                  <span style={styles.infoValue}>{profileData.emergencyContact.name}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Relationship</span>
                  <span style={styles.infoValue}>
                    {profileData.emergencyContact.relationship
                      ? profileData.emergencyContact.relationship.charAt(0).toUpperCase() +
                        profileData.emergencyContact.relationship.slice(1)
                      : <span style={styles.emptyState}>Not specified</span>}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Phone</span>
                  <span style={styles.infoValue}>
                    {profileData.emergencyContact.phone || <span style={styles.emptyState}>Not provided</span>}
                  </span>
                </div>
                {profileData.emergencyContact.email && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Email</span>
                    <span style={styles.infoValue}>{profileData.emergencyContact.email}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Medical Information */}
          <div style={{ ...styles.section, ...styles.medicalSection }}>
            <h3 style={styles.sectionTitle}>🏥 Medical Information</h3>
            <div style={styles.sectionContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Blood Type</span>
                <span style={styles.infoValue}>{profileData?.bloodType || <span style={styles.emptyState}>Not provided</span>}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Height</span>
                <span style={styles.infoValue}>{profileData?.height || <span style={styles.emptyState}>Not provided</span>}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Weight</span>
                <span style={styles.infoValue}>{profileData?.weight || <span style={styles.emptyState}>Not provided</span>}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Primary Care</span>
                <span style={styles.infoValue}>
                  {profileData?.primaryCarePhysician || <span style={styles.emptyState}>Not provided</span>}
                </span>
              </div>
            </div>
          </div>

          {/* Insurance Information */}
          {profileData?.insuranceProvider && (
            <div style={{ ...styles.section, ...styles.insuranceSection }}>
              <h3 style={styles.sectionTitle}>📋 Insurance Information</h3>
              <div style={styles.sectionContent}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Provider</span>
                  <span style={styles.infoValue}>{profileData.insuranceProvider}</span>
                </div>
                {profileData.policyNumber && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Policy Number</span>
                    <span style={styles.infoValue}>{profileData.policyNumber}</span>
                  </div>
                )}
                {profileData.groupNumber && (
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Group Number</span>
                    <span style={styles.infoValue}>{profileData.groupNumber}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lifestyle Information */}
          <div style={{ ...styles.section, ...styles.lifestyleSection }}>
            <h3 style={styles.sectionTitle}>🏃‍♂️ Lifestyle Information</h3>
            <div style={styles.sectionContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Smoking</span>
                <span style={styles.infoValue}>
                  {profileData?.smoking
                    ? profileData.smoking.charAt(0).toUpperCase() + profileData.smoking.slice(1)
                    : <span style={styles.emptyState}>Not provided</span>}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Alcohol</span>
                <span style={styles.infoValue}>
                  {profileData?.alcohol
                    ? profileData.alcohol.charAt(0).toUpperCase() + profileData.alcohol.slice(1)
                    : <span style={styles.emptyState}>Not provided</span>}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Exercise</span>
                <span style={styles.infoValue}>
                  {profileData?.exercise
                    ? profileData.exercise.charAt(0).toUpperCase() + profileData.exercise.slice(1)
                    : <span style={styles.emptyState}>Not provided</span>}
                </span>
              </div>
            </div>
          </div>

          {/* Communication Preferences */}
          <div style={{ ...styles.section, ...styles.preferencesSection }}>
            <h3 style={styles.sectionTitle}>🌐 Communication Preferences</h3>
            <div style={styles.sectionContent}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Language</span>
                <span style={styles.infoValue}>
                  {profileData?.preferredLanguage
                    ? profileData.preferredLanguage.charAt(0).toUpperCase() + profileData.preferredLanguage.slice(1)
                    : <span style={styles.emptyState}>Not specified</span>}
                </span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Contact Methods</span>
                <span style={styles.infoValue}>
                  {profileData?.communicationPreferences?.length > 0 ? (
                    <div>
                      {profileData.communicationPreferences.map((method) => (
                        <span key={method} style={styles.tag}>
                          {method === "email" && "📧"}
                          {method === "sms" && "📱"}
                          {method === "phone-call" && "📞"}
                          {method === "portal" && "🌐"}
                          {method.replace("-", " ").charAt(0).toUpperCase() + method.slice(1).replace("-", " ")}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={styles.emptyState}>Not specified</span>
                  )}
                </span>
              </div>
              {profileData?.accessibilityNeeds && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Accessibility</span>
                  <span style={styles.infoValue}>{profileData.accessibilityNeeds}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Medical History Section - Full Width */}
        {(profileData?.allergies ||
          profileData?.medications ||
          profileData?.medicalConditions ||
          profileData?.surgeries ||
          profileData?.familyHistory) && (
          <div style={{ ...styles.section, marginBottom: "24px" }}>
            <h3 style={styles.sectionTitle}>📝 Medical History</h3>
            <div style={styles.sectionContent}>
              {profileData.allergies && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ ...styles.infoLabel, marginBottom: "8px" }}>Allergies</div>
                  <div style={{ ...styles.infoValue, whiteSpace: "pre-wrap", textAlign: "left" }}>
                    {profileData.allergies}
                  </div>
                </div>
              )}
              {profileData.medications && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ ...styles.infoLabel, marginBottom: "8px" }}>Current Medications</div>
                  <div style={{ ...styles.infoValue, whiteSpace: "pre-wrap", textAlign: "left" }}>
                    {profileData.medications}
                  </div>
                </div>
              )}
              {profileData.medicalConditions && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ ...styles.infoLabel, marginBottom: "8px" }}>Medical Conditions</div>
                  <div style={{ ...styles.infoValue, whiteSpace: "pre-wrap", textAlign: "left" }}>
                    {profileData.medicalConditions}
                  </div>
                </div>
              )}
              {profileData.surgeries && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ ...styles.infoLabel, marginBottom: "8px" }}>Previous Surgeries</div>
                  <div style={{ ...styles.infoValue, whiteSpace: "pre-wrap", textAlign: "left" }}>
                    {profileData.surgeries}
                  </div>
                </div>
              )}
              {profileData.familyHistory && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ ...styles.infoLabel, marginBottom: "8px" }}>Family Medical History</div>
                  <div style={{ ...styles.infoValue, whiteSpace: "pre-wrap", textAlign: "left" }}>
                    {profileData.familyHistory}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Profile Metadata */}
        <div style={{ textAlign: "center", color: "#9ca3af", fontSize: "12px", marginTop: "32px" }}>
          {profileData?.profileCompletedAt && <p>Profile created on {formatDate(profileData.profileCompletedAt)}</p>}
          {profileData?.lastUpdated && <p>Last updated on {formatDate(profileData.lastUpdated)}</p>}
        </div>
      </div>
    </div>
  );
};

export default PatientProfileView;
